import { Locale, Workflow, WorkflowRunGroup, WorkflowTask } from '@aero-platform/shared'
import { DataTableExpandedRows, DataTableRowExpansionTemplate } from 'primereact/datatable'
import TaskList from './TaskList'
import { CollapseIcon, CopyIcon, ExpandIcon, LoadingIcon, PlusIcon } from '../Icons'
import { RunStatusCircle } from './StatusCircle'
import { WorkflowStatusDef } from '../../utils/constants'
import { formatDateWithLocale } from '../../utils/intl'
import { RunStatus } from '../../types/types'
import { Button } from 'primereact/button'
import toast from 'react-hot-toast'

export const getMatchingWorkflowRunGroup = (
  workflowRunGroups: WorkflowRunGroup[] | undefined,
  name: string
): WorkflowRunGroup | null => {
  if (workflowRunGroups === undefined) {
    return null
  }

  for (let i = 0; i < workflowRunGroups.length; i++) {
    let workflowRunGroup: WorkflowRunGroup = workflowRunGroups[i]

    //assuming 1 workflow w/ inner vehicleState per WorkflowRunGroup
    if (workflowRunGroup.runGroupCase.name === name) {
      return workflowRunGroup
    }
  }

  return null
}

//From Ted: If any task is in error, the workflow is in error. Otherwise, the workflow status is the status of the most recently updated task.
export const determineWorkflowStatus = (
  tasks: WorkflowTask[] | undefined,
  fallback: RunStatus = RunStatus.UNSPECIFIED
): RunStatus => {
  if (!tasks || tasks.length === 0) return fallback
  let latestStatus = fallback
  let latestTS = 0
  for (const task of tasks) {
    if (task.status && task.status.runStatus === RunStatus.ERROR) {
      return RunStatus.ERROR
    }
    if (task.timeUpdated && task.timeUpdated > latestTS) {
      latestTS = task.timeUpdated
      latestStatus = task.status?.runStatus ?? RunStatus.UNSPECIFIED
    }
  }
  return latestStatus
}

// Determines the status of a WorkflowRunGroup based on its workflows.
// If any workflow has error, returns ERROR. If all workflows have the same status, returns that status.
// Otherwise, returns the existing overallStatus of the group.
export const determineWorkflowGroupStatus = (
  workflowRunGroup: WorkflowRunGroup | null | undefined
): RunStatus => {
  if (!workflowRunGroup?.workflow || workflowRunGroup.workflow.length === 0) {
    return workflowRunGroup?.overallStatus ?? RunStatus.UNSPECIFIED
  }

  // If any workflow is in error, the case is in error.
  // Otherwise, the case status is the status of the most recently updated workflow.
  let latestStatus = RunStatus.UNSPECIFIED
  let latestTime = 0

  for (const workflow of workflowRunGroup.workflow) {
    const workflowStatus = determineWorkflowStatus(workflow.workflowTasks, workflow.overallStatus)

    if (workflowStatus === RunStatus.ERROR) {
      return RunStatus.ERROR
    }

    const wfTime = workflow.timeUpdated ?? 0
    if (wfTime >= latestTime) {
      latestTime = wfTime
      latestStatus = workflowStatus
    }
  }

  return latestStatus
}

const isDynamicWorkflow = (workflow: Workflow): boolean => {
  const allowedDynamicWorkflowNames = ['Ensight']
  // Check if the workflow has a task that can add dynamic workflows
  return workflow.workflowTasks.some(task =>
    allowedDynamicWorkflowNames.some(name =>
      task.name.toLocaleLowerCase().includes(name.toLocaleLowerCase())
    )
  )
}

const StateRowExpansionTemplate = (
  workflowSubmissionId: string,
  workflowRunGroups: WorkflowRunGroup[],
  expandedWorkflows: DataTableExpandedRows,
  setExpandedWorkflows,
  userLocale: Locale,
  isProcessing: boolean,
  showAdditionalReferenceRunsModal: string,
  setShowAdditionalReferenceRunsModal,
  supersededIds?: Set<string>
) => {
  return (rowData: WorkflowRunGroup, options: DataTableRowExpansionTemplate) => {
    if (!workflowRunGroups || workflowRunGroups.length === 0) {
      return null
    }

    // Use rowData directly since dataKey is systemRunId — this ensures we get
    // the exact WorkflowRunGroup for this row, not just the first name match
    const workflowRunGroup: WorkflowRunGroup | null =
      workflowRunGroups.find(wrg => wrg.systemRunId === rowData.systemRunId) || null

    if (
      !workflowRunGroup ||
      workflowRunGroup.workflow === undefined ||
      workflowRunGroup.workflow.length === 0
    ) {
      return null
    }

    return workflowRunGroup?.workflow.map((workflow: Workflow, index: number) => {
      const rowName: string = workflow?.systemRunId
      return (
        <section className="flex flex-col items-stretch ml-8">
          <div className="grid grid-cols-12 pb-8">
            <div
              className="col-span-12 flex items-center space-x-2 text-sm text-gm-blue dark:text-light-blue cursor-pointer"
              onClick={() => {
                const copyExpandedRows: DataTableExpandedRows = { ...expandedWorkflows }
                if (expandedWorkflows[rowName]) {
                  // if rowName exists in expandedRows
                  // delete rowName from expandedRows
                  delete copyExpandedRows[rowName]
                } else {
                  // if rowName doesnt exist, add rowName to expandedRows
                  copyExpandedRows[rowName] = true
                }
                setExpandedWorkflows(copyExpandedRows)
              }}>
              {expandedWorkflows[rowName] ? (
                <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              ) : (
                <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              )}
              <div className="font-semibold">Workflow #{index + 1}:</div>
              <div>{workflow?.name}</div>
            </div>

            <div className="col-span-2 px-8 flex items-center">
              <RunStatusCircle
                status={determineWorkflowStatus(workflow?.workflowTasks, workflow?.overallStatus)}
              />
              <div className="flex flex-col">
                <div className="text-[10px]">Status</div>
                <div>
                  {
                    WorkflowStatusDef[
                      determineWorkflowStatus(workflow?.workflowTasks, workflow?.overallStatus)
                    ].name
                  }
                </div>
              </div>
            </div>

            <div className="col-span-2 px-8 ">
              <div className="text-[10px]">System Run ID</div>
              <div>{workflow?.systemRunId}</div>
            </div>

            <div className="col-span-4">
              <div className="text-[10px]">Description</div>
              <div>{workflow?.description}</div>
            </div>

            <div className="col-span-2 px-8 ">
              <div className="text-[10px]">Created</div>
              <div>
                {workflow?.timeCreated
                  ? formatDateWithLocale(workflow?.timeCreated, userLocale)
                  : 'N/A'}
              </div>
            </div>

            <div className="col-span-2 px-8 ">
              <div className="text-[10px]">Updated</div>
              <div>
                {workflow?.timeUpdated
                  ? formatDateWithLocale(workflow?.timeUpdated, userLocale)
                  : 'N/A'}
              </div>
            </div>
          </div>
          {expandedWorkflows[rowName] && (
            <div className="pl-8">
              <TaskList
                tasks={workflow?.workflowTasks}
                runId={workflowSubmissionId}
                workflowRunGroupSysId={workflowRunGroup.systemRunId}
                workflowSystemRunId={workflow.systemRunId}
                disabled={supersededIds?.has(workflowRunGroup.systemRunId)}
              />
              {/* Check if this task can add dynamic workflows */}
              {isDynamicWorkflow(workflow) && !isProcessing && (
                // Align the button to the right of the task list
                <div className="flex justify-end">
                  <Button
                    className="flex items-start space-x-2 bg-gm-blue dark:bg-gm-blue text-white font-semibold"
                    onClick={() =>
                      setShowAdditionalReferenceRunsModal(workflowRunGroup?.systemRunId)
                    }
                    disabled={isProcessing}>
                    {isProcessing && <LoadingIcon className="w-4 h-4 fill-white animate-spin" />}
                    <span>Add Additional Delta Generation Task(s)</span>
                    <PlusIcon className="w-4 h-4 fill-white" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      )
    })
  }
}

export default StateRowExpansionTemplate
